import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import api from '../../services/api';
import { patientService } from '../../services/ris';
import { useThemeMode } from '../../contexts/ThemeModeContext';

const GENDERS = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' }
];

const STATES = [
  { value: 'CA', label: 'California' },
  { value: 'NY', label: 'New York' },
  { value: 'TX', label: 'Texas' },
  { value: 'FL', label: 'Florida' },
  { value: 'IL', label: 'Illinois' },
  { value: 'WA', label: 'Washington' },
  { value: 'Lagos', label: 'Lagos' },
  { value: 'Gauteng', label: 'Gauteng' },
  { value: 'Other', label: 'Other' }
];

const COUNTRIES = [
  { value: 'US', label: 'United States' },
  { value: 'UK', label: 'United Kingdom' },
  { value: 'CA', label: 'Canada' },
  { value: 'AU', label: 'Australia' },
  { value: 'NG', label: 'Nigeria' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' }
];

const EditPatientMetadataDialog = ({ open, onClose, study, onSuccess }) => {
  const [formData, setFormData] = useState({
    patientName: '',
    patientId: '',
    patientSex: '',
    patientBirthDate: '',
    studyDescription: '',
    accessionNumber: '',
    institutionName: '',
    referringPhysicianName: '',
    studyDate: '',
    studyTime: '',
    studyId: '',
    telephone: '',
    email: '',
    address: '',
    state: '',
    country: '',
  });

  const [loading, setLoading] = useState(false);
  const [loadingPatient, setLoadingPatient] = useState(false);
  const [patientRegistryId, setPatientRegistryId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (study && open) {
      // Format dates for input fields (YYYY-MM-DD format)
      const formatDateForInput = (date) => {
        if (!date) return '';
        const d = new Date(date);
        if (isNaN(d.getTime())) return '';
        return d.toISOString().split('T')[0];
      };

      // Format time for input (HH:MM format)
      const formatTimeForInput = (time) => {
        if (!time) return '';
        // If time is in format HHMMSS, convert to HH:MM
        if (time.length === 6 && !time.includes(':')) {
          return `${time.slice(0, 2)}:${time.slice(2, 4)}`;
        }
        return time;
      };

      setFormData({
        patientName: study.patientName || '',
        patientId: study.patientId || '',
        patientSex: study.patientSex || '',
        patientBirthDate: formatDateForInput(study.patientBirthDate),
        studyDescription: study.studyDescription || '',
        accessionNumber: study.accessionNumber || '',
        institutionName: study.institutionName || '',
        referringPhysicianName: study.referringPhysicianName || '',
        studyDate: formatDateForInput(study.studyDate),
        studyTime: formatTimeForInput(study.studyTime),
        studyId: study.studyId || '',
        telephone: '',
        email: '',
        address: '',
        state: '',
        country: '',
      });
      setError('');
      setSuccess(false);

      if (study.patientId) {
        setLoadingPatient(true);
        patientService.getByMrn(study.patientId)
          .then(res => {
            if (res.data && res.data.patient) {
              const p = res.data.patient;
              setPatientRegistryId(p._id);
              
              let street = '';
              let state = '';
              let country = '';
              if (p.address) {
                const parts = p.address.split(',').map(part => part.trim());
                if (parts.length >= 3) {
                  street = parts.slice(0, parts.length - 2).join(', ');
                  state = parts[parts.length - 2];
                  country = parts[parts.length - 1];
                } else if (parts.length === 2) {
                  street = parts[0];
                  state = parts[1];
                } else {
                  street = p.address;
                }
              }

              setFormData(prev => ({
                ...prev,
                telephone: p.telephone || p.phone || '',
                email: p.email || '',
                address: street,
                state: state,
                country: country,
              }));
            } else {
              setPatientRegistryId(null);
            }
          })
          .catch(err => {
            console.warn('Could not fetch patient registry details:', err);
            setPatientRegistryId(null);
          })
          .finally(() => {
            setLoadingPatient(false);
          });
      } else {
        setPatientRegistryId(null);
      }
    }
  }, [study, open]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'country' ? { state: '' } : {}),
    }));
  };

  const parsePatientName = (fullName) => {
    if (!fullName) return { firstName: '', lastName: '' };
    if (fullName.includes('^')) {
      const parts = fullName.split('^');
      return {
        lastName: parts[0] || '',
        firstName: parts[1] || ''
      };
    } else {
      const parts = fullName.trim().split(/\s+/);
      if (parts.length === 1) {
        return { firstName: parts[0], lastName: '' };
      }
      return {
        firstName: parts[0],
        lastName: parts.slice(1).join(' ')
      };
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Prepare data for API
      const updateData = {};
      
      if (formData.patientName) updateData.patientName = formData.patientName;
      if (formData.patientId) updateData.patientId = formData.patientId;
      if (formData.patientSex) updateData.patientSex = formData.patientSex;
      if (formData.patientBirthDate) updateData.patientBirthDate = formData.patientBirthDate;
      if (formData.studyDescription !== undefined) updateData.studyDescription = formData.studyDescription;
      if (formData.accessionNumber !== undefined) updateData.accessionNumber = formData.accessionNumber;
      if (formData.institutionName !== undefined) updateData.institutionName = formData.institutionName;
      if (formData.referringPhysicianName !== undefined) updateData.referringPhysicianName = formData.referringPhysicianName;
      if (formData.studyDate) updateData.studyDate = formData.studyDate;
      if (formData.studyTime) {
        // Convert HH:MM to HHMMSS format
        const timeStr = formData.studyTime.replace(/:/g, '');
        updateData.studyTime = timeStr.length === 4 ? `${timeStr}00` : timeStr;
      }
      if (formData.studyId !== undefined) updateData.studyId = formData.studyId;

      // 1. Update Patient registry in MongoDB if exists
      if (patientRegistryId) {
        const { firstName, lastName } = parsePatientName(formData.patientName);
        const addressParts = [formData.address.trim(), formData.state, formData.country].filter(Boolean);
        const serializedAddress = addressParts.join(', ');

        const patientUpdate = {
          sex: formData.patientSex,
          birthDate: formData.patientBirthDate || undefined,
          telephone: formData.telephone,
          email: formData.email || undefined,
          address: serializedAddress,
        };
        if (firstName) patientUpdate.firstName = firstName;
        if (lastName) patientUpdate.lastName = lastName;

        await patientService.update(patientRegistryId, patientUpdate);
      }

      // 2. Update PACS/Study metadata
      const response = await api.patch(`/studies/${study._id}/patient-metadata`, updateData);

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          if (onSuccess) {
            onSuccess(response.data.study);
          }
          onClose();
        }, 1500);
      } else {
        setError(response.data.error || 'Failed to update metadata');
      }
    } catch (err) {
      console.error('Error updating patient metadata:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update patient metadata');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const inputBg = isDark ? '#121212' : '#FFFFFF';
  const cardBorderColor = isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0';
  const cardBorder = `1px solid ${cardBorderColor}`;
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#64748B';
  const hoverBgSubtle = isDark ? 'rgba(255, 255, 255, 0.05)' : '#F8FAFC';

  // Satoshi Typography Style Helpers
  const satoshiBold = {
    fontFamily: '"Satoshi", sans-serif',
    fontWeight: 700,
  };

  const labelStyle = {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: textColorPrimary,
    mb: 0.5,
    fontFamily: '"Satoshi", sans-serif',
    display: 'flex',
    alignItems: 'center',
  };

  const textFieldSx = (multiline = false) => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: '6px',
      backgroundColor: inputBg,
      height: multiline ? 'auto' : '32px',
      border: cardBorder,
      transition: 'border-color 0.2s',
      '& fieldset': {
        border: 'none',
      },
      '&:hover': {
        borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : '#CBD5E1',
      },
      '&.Mui-focused': {
        borderColor: '#0D9488',
        borderWidth: '1.5px',
      },
    },
    '& .MuiInputBase-input': {
      py: multiline ? 0.75 : 0.5,
      px: 1.25,
      fontSize: '0.8rem',
      color: textColorPrimary,
      fontWeight: 500,
      fontFamily: '"Satoshi", sans-serif',
      '&::placeholder': {
        color: '#94A3B8',
        opacity: 1,
      }
    },
    '& .MuiFormHelperText-root': {
      fontFamily: '"Satoshi", sans-serif',
      mx: 0,
      mt: 0.25,
      fontSize: '0.7rem',
    }
  });

  const selectSx = {
    ...textFieldSx(false),
    '& .MuiSelect-select': {
      py: 0,
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.8rem',
      color: textColorPrimary,
      fontWeight: 500,
      fontFamily: '"Satoshi", sans-serif',
    }
  };

  const cardStyle = {
    p: 1.5,
    mb: 1.25,
    border: cardBorder,
    boxShadow: 'none',
    borderRadius: '10px',
    bgcolor: cardBg,
  };

  const cardHeaderBox = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 1,
  };

  const iconCircleStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 26,
    height: 26,
    borderRadius: '6px',
    color: isDark ? '#2DD4BF' : '#0D9488',
    bgcolor: isDark ? 'rgba(13, 148, 136, 0.15)' : '#F0FDFA',
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '95vw', sm: '92vw', md: '900px', lg: '940px' },
          maxWidth: '960px !important',
          maxHeight: '88vh',
          borderRadius: '12px',
          p: 2,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
          backgroundColor: cardBg,
          border: cardBorder,
          fontFamily: '"Satoshi", sans-serif',
        }
      }}
    >
      <DialogTitle sx={{ p: 0, pb: 1.25 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1.25}>
            <Box sx={iconCircleStyle}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </Box>
            <Typography variant="h6" sx={{ ...satoshiBold, color: textColorPrimary, fontSize: '0.98rem' }}>
              Edit Patient & Study Metadata
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={loading} size="small" sx={{ color: textColorSecondary }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, py: 0.5, overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.25, py: 0.25, borderRadius: '6px', fontFamily: '"Satoshi", sans-serif', fontWeight: 600, fontSize: '0.78rem' }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 1.25, py: 0.25, borderRadius: '6px', fontFamily: '"Satoshi", sans-serif', fontWeight: 600, fontSize: '0.78rem' }}>
            Patient metadata updated successfully! Changes have been applied to both the database and Orthanc.
          </Alert>
        )}

        <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
          {/* Left Column: Patient Information */}
          <Grid item xs={12} md={6}>
            <Paper sx={cardStyle}>
              <Box sx={cardHeaderBox}>
                <Box sx={iconCircleStyle}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </Box>
                <Typography variant="subtitle1" sx={{ ...satoshiBold, color: textColorPrimary, fontSize: '0.85rem' }}>
                  Patient Information
                </Typography>
              </Box>

              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={labelStyle}>Patient Name</Typography>
                  <TextField
                    fullWidth
                    value={formData.patientName}
                    onChange={handleChange('patientName')}
                    disabled={loading}
                    placeholder="LAST^FIRST^MIDDLE"
                    sx={textFieldSx(false)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={labelStyle}>Patient ID</Typography>
                  <TextField
                    fullWidth
                    value={formData.patientId}
                    onChange={handleChange('patientId')}
                    disabled={loading}
                    sx={textFieldSx(false)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={labelStyle}>Patient Sex</Typography>
                  <TextField
                    fullWidth
                    select
                    value={formData.patientSex}
                    onChange={handleChange('patientSex')}
                    disabled={loading}
                    sx={selectSx}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) => {
                        if (!selected) return <span style={{ color: '#94A3B8' }}>Select Gender</span>;
                        return GENDERS.find(g => g.value === selected)?.label || selected;
                      }
                    }}
                  >
                    <MenuItem value="" sx={{ fontFamily: '"Satoshi", sans-serif' }}>Not Specified</MenuItem>
                    {GENDERS.map(g => (
                      <MenuItem key={g.value} value={g.value} sx={{ fontFamily: '"Satoshi", sans-serif' }}>
                        {g.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={labelStyle}>Patient Birth Date</Typography>
                  <TextField
                    fullWidth
                    type="date"
                    value={formData.patientBirthDate}
                    onChange={handleChange('patientBirthDate')}
                    disabled={loading}
                    sx={textFieldSx(false)}
                    InputLabelProps={{ shrink: false }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={labelStyle}>Phone Number</Typography>
                  <TextField
                    fullWidth
                    value={formData.telephone}
                    onChange={handleChange('telephone')}
                    disabled={loading || loadingPatient}
                    sx={textFieldSx(false)}
                    InputProps={loadingPatient ? {
                      endAdornment: <CircularProgress size={14} sx={{ color: '#0D9488' }} />
                    } : null}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={labelStyle}>Email Address</Typography>
                  <TextField
                    fullWidth
                    value={formData.email}
                    onChange={handleChange('email')}
                    disabled={loading || loadingPatient}
                    sx={textFieldSx(false)}
                    InputProps={loadingPatient ? {
                      endAdornment: <CircularProgress size={14} sx={{ color: '#0D9488' }} />
                    } : null}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={labelStyle}>Address</Typography>
                  <TextField
                    fullWidth
                    value={formData.address}
                    onChange={handleChange('address')}
                    disabled={loading || loadingPatient}
                    placeholder="Enter street address"
                    sx={textFieldSx(false)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={labelStyle}>Country</Typography>
                  <TextField
                    fullWidth
                    select
                    value={formData.country}
                    onChange={handleChange('country')}
                    disabled={loading || loadingPatient}
                    sx={selectSx}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) => {
                        if (!selected) return <span style={{ color: '#94A3B8' }}>Select Country</span>;
                        return COUNTRIES.find(c => c.value === selected)?.label || selected;
                      }
                    }}
                  >
                    <MenuItem value="" sx={{ fontFamily: '"Satoshi", sans-serif' }}>Select Country</MenuItem>
                    {COUNTRIES.map(c => (
                      <MenuItem key={c.value} value={c.value} sx={{ fontFamily: '"Satoshi", sans-serif' }}>
                        {c.label}
                      </MenuItem>
                    ))}
                    {formData.country && !COUNTRIES.some(c => c.value === formData.country) && (
                      <MenuItem value={formData.country} sx={{ fontFamily: '"Satoshi", sans-serif' }}>
                        {formData.country}
                      </MenuItem>
                    )}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={labelStyle}>State</Typography>
                  <TextField
                    fullWidth
                    select
                    value={formData.state}
                    onChange={handleChange('state')}
                    disabled={loading || loadingPatient || !formData.country}
                    sx={selectSx}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) => {
                        if (!selected) return <span style={{ color: '#94A3B8' }}>Select State</span>;
                        return STATES.find(s => s.value === selected)?.label || selected;
                      }
                    }}
                  >
                    <MenuItem value="" sx={{ fontFamily: '"Satoshi", sans-serif' }}>Select State</MenuItem>
                    {STATES.map(s => (
                      <MenuItem key={s.value} value={s.value} sx={{ fontFamily: '"Satoshi", sans-serif' }}>
                        {s.label}
                      </MenuItem>
                    ))}
                    {formData.state && !STATES.some(s => s.value === formData.state) && (
                      <MenuItem value={formData.state} sx={{ fontFamily: '"Satoshi", sans-serif' }}>
                        {formData.state}
                      </MenuItem>
                    )}
                  </TextField>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Right Column: Study Details & Institution */}
          <Grid item xs={12} md={6}>
            {/* Study Information Section */}
            <Paper sx={cardStyle}>
              <Box sx={cardHeaderBox}>
                <Box sx={iconCircleStyle}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                </Box>
                <Typography variant="subtitle1" sx={{ ...satoshiBold, color: textColorPrimary, fontSize: '0.85rem' }}>
                  Study Information
                </Typography>
              </Box>

              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography sx={labelStyle}>Study Description</Typography>
                  <TextField
                    fullWidth
                    value={formData.studyDescription}
                    onChange={handleChange('studyDescription')}
                    disabled={loading}
                    placeholder="e.g. CT Abdomen, Chest X-Ray"
                    sx={textFieldSx(false)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={labelStyle}>Accession Number</Typography>
                  <TextField
                    fullWidth
                    value={formData.accessionNumber}
                    onChange={handleChange('accessionNumber')}
                    disabled={loading}
                    sx={textFieldSx(false)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={labelStyle}>Study ID</Typography>
                  <TextField
                    fullWidth
                    value={formData.studyId}
                    onChange={handleChange('studyId')}
                    disabled={loading}
                    sx={textFieldSx(false)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={labelStyle}>Study Date</Typography>
                  <TextField
                    fullWidth
                    type="date"
                    value={formData.studyDate}
                    onChange={handleChange('studyDate')}
                    disabled={loading}
                    sx={textFieldSx(false)}
                    InputLabelProps={{ shrink: false }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={labelStyle}>Study Time</Typography>
                  <TextField
                    fullWidth
                    type="time"
                    value={formData.studyTime}
                    onChange={handleChange('studyTime')}
                    disabled={loading}
                    sx={textFieldSx(false)}
                    InputLabelProps={{ shrink: false }}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Institution Information Section */}
            <Paper sx={cardStyle}>
              <Box sx={cardHeaderBox}>
                <Box sx={iconCircleStyle}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <line x1="9" y1="3" x2="9" y2="21" />
                  </svg>
                </Box>
                <Typography variant="subtitle1" sx={{ ...satoshiBold, color: textColorPrimary, fontSize: '0.85rem' }}>
                  Institution & Referring Physician
                </Typography>
              </Box>

              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={labelStyle}>Institution Name</Typography>
                  <TextField
                    fullWidth
                    value={formData.institutionName}
                    onChange={handleChange('institutionName')}
                    disabled={loading}
                    sx={textFieldSx(false)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={labelStyle}>Referring Physician Name</Typography>
                  <TextField
                    fullWidth
                    value={formData.referringPhysicianName}
                    onChange={handleChange('referringPhysicianName')}
                    disabled={loading}
                    placeholder="LAST^FIRST^MIDDLE"
                    sx={textFieldSx(false)}
                  />
                </Grid>
              </Grid>
            </Paper>

            {study?.studyInstanceUID && (
              <Box sx={{ p: 1, bgcolor: hoverBgSubtle, borderRadius: '6px', border: cardBorder }}>
                <Typography variant="caption" sx={{ fontFamily: '"Satoshi", sans-serif', color: textColorSecondary, fontWeight: 500, fontSize: '0.7rem' }}>
                  UID: {study.studyInstanceUID}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 0, pt: 1.5, pb: 0, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={loading}
          sx={{
            textTransform: 'none',
            borderRadius: '6px',
            px: 2,
            height: '32px',
            fontWeight: 700,
            fontFamily: '"Satoshi", sans-serif',
            borderColor: cardBorderColor,
            color: textColorPrimary,
            bgcolor: cardBg,
            fontSize: '0.8rem',
            boxShadow: 'none',
            '&:hover': {
              borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : '#CBD5E1',
              backgroundColor: hoverBgSubtle,
              boxShadow: 'none',
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{
            textTransform: 'none',
            borderRadius: '6px',
            px: 2.25,
            height: '32px',
            fontWeight: 700,
            fontFamily: '"Satoshi", sans-serif',
            backgroundColor: '#00665C',
            color: '#FFFFFF',
            fontSize: '0.8rem',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#004D47',
              boxShadow: 'none',
            }
          }}
        >
          {loading ? 'Updating...' : 'Update Metadata'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPatientMetadataDialog;
